// Начален екран на наемодателя/пропърти мениджъра: обобщение на портфейла,
// парите за месеца и какво чака действие. Всяка карта води към екрана,
// на който проблемът се решава.

import React, { useCallback, useState } from 'react';
import { Modal, Pressable, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { api, money } from '../../api';
import { useAuth } from '../../AuthContext';
import { Badge, Button, Card, COLORS, Label, Muted, Screen, SectionLabel, SP, StatTile, Title } from '../../ui';
import { showError } from '../../toast';
import { getLocale, t } from '../../i18n';

// Бърз бутон-плочка за начесто ползвано действие
function QuickAction({ icon, label, onPress }: { icon: string; label: string; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => ({
        flex: 1,
        minWidth: '30%',
        backgroundColor: COLORS.card,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 14,
        paddingVertical: 14,
        alignItems: 'center',
        gap: 6,
        opacity: pressed ? 0.6 : 1,
      })}
    >
      <Text style={{ fontSize: 24 }}>{icon}</Text>
      <Text style={{ fontSize: 12, fontWeight: '600', color: COLORS.text, textAlign: 'center' }}>{label}</Text>
    </Pressable>
  );
}

// Ред „чака действие“ — показва се само когато има какво да се свърши
function TodoRow({
  count,
  label,
  onPress,
  urgent,
}: {
  count: number;
  label: string;
  onPress: () => void;
  urgent?: boolean;
}) {
  if (!count) return null;
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 12,
        opacity: pressed ? 0.6 : 1,
      })}
    >
      <View
        style={{
          minWidth: 26,
          height: 26,
          borderRadius: 13,
          paddingHorizontal: 7,
          backgroundColor: urgent ? COLORS.danger : COLORS.primary,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>{count}</Text>
      </View>
      <Text style={{ flex: 1, color: COLORS.text, fontSize: 15 }}>{label}</Text>
      <Text style={{ color: COLORS.muted, fontSize: 18 }}>›</Text>
    </Pressable>
  );
}

export default function DashboardScreen({ navigation }: any) {
  const { user } = useAuth();
  const [data, setData] = useState<any>(null);
  const [restricted, setRestricted] = useState(false);
  // Поканата за отзиви за минали наематели: '1' = отвори веднага (нова
  // регистрация), 'shown'/null = картата стои, 'done' = скрита (на това
  // устройство). Показва се и в уеб версията, независимо къде е регистрацията.
  const [reviewsPrompt, setReviewsPrompt] = useState<string | null>('loading');

  useFocusEffect(
    useCallback(() => {
      api('/apartments/dashboard').then(setData).catch(() => {});
      api('/billing/subscription').then((r) => setRestricted(!r.access?.active)).catch(() => {});
      AsyncStorage.getItem('pastReviewsPrompt').then(setReviewsPrompt);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );

  if (!data) {
    return (
      <Screen>
        <Title>{user?.firstName ? t('Здравейте, {name}', { name: user.firstName }) : t('Здравейте')}</Title>
        <Muted>{t('Зареждане…')}</Muted>
      </Screen>
    );
  }

  const { portfolio, money: m, todo, endingSoon, todoApartments } = data;

  // „+ Наемател" / „Нова сметка": при 1 имот води направо там, иначе към
  // списъка с имоти да се избере — така действието е на 1–2 клика.
  const quickForApartment = async (screen: string) => {
    try {
      const r = await api('/apartments');
      const list = (r.apartments ?? []).filter((a: any) => a.status !== 'ARCHIVED');
      if (list.length === 1) navigation.navigate(screen, { apartmentId: list[0].id });
      else navigation.navigate('Apartments');
    } catch (e: any) {
      showError(e.message);
    }
  };

  // Води направо при имота, ако е само един; иначе към списъка
  const goToApartments = (list: any[]) =>
    list?.length === 1
      ? navigation.navigate('ApartmentDetail', { id: list[0].id })
      : navigation.navigate('Apartments');
  const unrated = data.unratedEndedTenancies ?? [];
  const todoTotal =
    todo.awaitingConfirmation +
    todo.openIssues +
    (todo.issuesAwaitingConfirm ?? 0) +
    todo.pendingListings +
    todo.unpaidBills +
    unrated.length;

  const closePrompt = (value: 'shown' | 'done') => {
    AsyncStorage.setItem('pastReviewsPrompt', value);
    setReviewsPrompt(value);
  };

  return (
    <Screen>
      {/* Поп-ъп веднага след регистрация: покана за отзиви за минали наематели */}
      <Modal visible={reviewsPrompt === '1'} transparent animationType="fade" onRequestClose={() => closePrompt('shown')}>
        <View style={{ flex: 1, backgroundColor: 'rgba(14,21,36,0.55)', justifyContent: 'center', padding: 24 }}>
          <View style={{ backgroundColor: COLORS.card, borderRadius: 18, padding: 22, maxWidth: 420, width: '100%', alignSelf: 'center' }}>
            <Text style={{ fontSize: 40, textAlign: 'center' }}>⭐</Text>
            <Text style={{ fontSize: 19, fontWeight: '700', color: COLORS.text, textAlign: 'center', marginTop: 8 }}>
              {t('Имали сте наематели преди?')}
            </Text>
            <Text style={{ color: COLORS.muted, textAlign: 'center', marginTop: 8, lineHeight: 21 }}>
              {t('Оставете отзив за миналите си наематели — минута на човек. Така следващият наемодател знае какво да очаква, а вашите кандидати се проверяват в по-пълна база.')}
            </Text>
            <Button
              title={t('Остави отзив')}
              onPress={() => {
                closePrompt('shown');
                navigation.navigate('PastTenants');
              }}
              style={{ alignSelf: 'stretch', marginTop: 16 }}
            />
            <Button title={t('Нямам / по-късно')} variant="ghost" onPress={() => closePrompt('shown')} />
          </View>
        </View>
      </Modal>

      <Text style={{ fontSize: 13, color: COLORS.muted, marginBottom: 2 }}>{t('Здравейте,')}</Text>
      <Title>{user?.firstName ? `${user.firstName} 👋` : '👋'}</Title>

      {restricted && (
        <Card style={{ borderColor: COLORS.danger, borderWidth: 2 }}>
          <Badge text={t('ОГРАНИЧЕН РЕЖИМ')} color={COLORS.danger} />
          <Muted>{t('Абонаментът е изтекъл — само преглед. Обявите са свалени.')}</Muted>
          <Button title={t('Поднови абонамента →')} onPress={() => navigation.navigate('Subscription')} />
        </Card>
      )}

      {/* ЧАКА ДЕЙСТВИЕ — най-важното, най-отгоре (action-first) */}
      {todoTotal === 0 ? (
        <Card style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <View style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: `${COLORS.success}18`, alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: 18 }}>✓</Text>
          </View>
          <Text style={{ flex: 1, color: COLORS.text, fontWeight: '600' }}>{t('Всичко е наред — няма нищо за одобряване.')}</Text>
        </Card>
      ) : (
        <Card style={{ borderColor: `${COLORS.danger}55`, borderWidth: 1.5, backgroundColor: `${COLORS.danger}0A` }}>
          <Text style={{ fontSize: 12, fontWeight: '800', letterSpacing: 0.6, color: COLORS.danger, textTransform: 'uppercase', marginBottom: 2 }}>
            {t('⚠ Чака действие')}
          </Text>
          <TodoRow
            urgent
            count={todo.issuesAwaitingConfirm ?? 0}
            label={t('отстранени нередности чакат потвърждение')}
            onPress={() => navigation.navigate('Issues')}
          />
          <TodoRow
            count={todo.awaitingConfirmation}
            label={
              todoApartments?.awaitingConfirmation?.length === 1
                ? t('плащане за потвърждение · {address}', { address: todoApartments.awaitingConfirmation[0].address })
                : t('плащания чакат „Получено“')
            }
            onPress={() => goToApartments(todoApartments?.awaitingConfirmation)}
          />
          <TodoRow count={todo.openIssues} label={t('нередности за решаване')} onPress={() => navigation.navigate('Issues')} />
          <TodoRow
            count={todo.unpaidBills}
            label={
              todoApartments?.unpaidBills?.length === 1
                ? t('неплатени сметки · {address}', { address: todoApartments.unpaidBills[0].address })
                : t('неплатени сметки')
            }
            onPress={() => goToApartments(todoApartments?.unpaidBills)}
          />
          <TodoRow
            count={todo.pendingListings}
            label={t('обяви чакат одобрение от модератор')}
            onPress={() => navigation.navigate('Listings')}
          />
          {unrated.map((u: any) => (
            <TodoRow
              key={u.tenancyId}
              count={1}
              label={t('оценете бившия наемател {name} · {address}', { name: u.tenantName ?? '', address: u.address })}
              onPress={() =>
                navigation.navigate('Review', { tenancyId: u.tenancyId, targetName: u.tenantName ?? u.address })
              }
            />
          ))}
        </Card>
      )}

      {/* Бързи действия — най-честото на 1 клик */}
      <SectionLabel>{t('Бързи действия')}</SectionLabel>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SP.sm, marginBottom: SP.md }}>
        <QuickAction icon="➕" label={t('Имот')} onPress={() => navigation.navigate('AddApartment')} />
        <QuickAction icon="👤" label={t('Наемател')} onPress={() => quickForApartment('AddTenant')} />
        {user?.role === 'AGENCY' ? (
          // Пропърти мениджърът управлява ЧУЖДИ имоти — собствениците ѝ трябват под ръка
          <QuickAction icon="🏠" label={t('Собственици')} onPress={() => navigation.navigate('Owners')} />
        ) : (
          <QuickAction icon="🛠️" label={t('Нередности')} onPress={() => navigation.navigate('Issues')} />
        )}
        <QuickAction icon="📄" label={t('Документи')} onPress={() => navigation.navigate('Documents')} />
        <QuickAction icon="⭐" label={t('Стари наематели')} onPress={() => navigation.navigate('PastTenants')} />
      </View>

      {reviewsPrompt !== 'loading' && reviewsPrompt !== 'done' && reviewsPrompt !== '1' && (
        <Card style={{ borderColor: COLORS.primary, borderWidth: 1.5 }}>
          <Label>{t('⭐ Имали сте наематели преди?')}</Label>
          <Muted>
            {t('Оставете отзив за миналите си наематели — минута на човек, а следващият наемодател ще ви е благодарен.')}
          </Muted>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Button title={t('Остави отзив')} onPress={() => navigation.navigate('PastTenants')} style={{ flex: 1 }} />
            <Button
              title={t('Скрий')}
              variant="ghost"
              onPress={() => {
                AsyncStorage.setItem('pastReviewsPrompt', 'done');
                setReviewsPrompt('done');
              }}
              style={{ flex: 1 }}
            />
          </View>
        </Card>
      )}

      {/* Компактни числа за един поглед (по макета) */}
      <SectionLabel>{t('Този месец')}</SectionLabel>
      <View style={{ flexDirection: 'row', gap: SP.sm, marginBottom: SP.md }}>
        <StatTile
          label={t('Получено')}
          value={money(m.collectedThisMonthCents)}
          hint={m.expectedThisMonthCents ? t('от {amount}', { amount: money(m.expectedThisMonthCents) }) : undefined}
          color={COLORS.success}
        />
        <StatTile
          label={t('Просрочено')}
          value={money(m.overdueCents)}
          hint={m.overdueCount ? t('{n} задълж.', { n: m.overdueCount }) : t('няма')}
          color={m.overdueCents > 0 ? COLORS.danger : COLORS.muted}
          onPress={m.overdueCents > 0 ? () => navigation.navigate('Apartments') : undefined}
        />
      </View>

      <SectionLabel>{t('Имоти')}</SectionLabel>
      <View style={{ flexDirection: 'row', gap: SP.sm, marginBottom: SP.md }}>
        <StatTile label={t('Общо')} value={String(portfolio.apartments)} onPress={() => navigation.navigate('Apartments')} />
        <StatTile label={t('Отдадени')} value={String(portfolio.occupied)} color={COLORS.success} />
        <StatTile
          label={t('Свободни')}
          value={String(portfolio.vacant)}
          color={portfolio.vacant > 0 ? COLORS.warn : COLORS.muted}
          onPress={() => navigation.navigate('Apartments')}
        />
      </View>

      {/* Изтичащи договори — с дадено предизвестие */}
      {endingSoon.length > 0 && (
        <Card style={{ borderColor: '#D97706', borderWidth: 1 }}>
          <Label>{t('Изтичащи договори')}</Label>
          <Muted>{t('Имоти с дадено предизвестие — скоро се освобождават.')}</Muted>
          {endingSoon.map((e: any) => (
            <Pressable
              key={e.tenancyId}
              onPress={() => navigation.navigate('ApartmentDetail', { id: e.apartmentId })}
              style={{ paddingVertical: 8, borderTopWidth: 1, borderTopColor: COLORS.border, marginTop: 6 }}
            >
              <Text style={{ color: COLORS.text, fontWeight: '600' }}>{e.address}</Text>
              <Muted>
                {e.city} · {t('свободен от {date}', { date: new Date(e.endsOn).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })}
              </Muted>
            </Pressable>
          ))}
        </Card>
      )}
    </Screen>
  );
}
